import { useEffect, useLayoutEffect, useMemo, useRef, useState, type CSSProperties } from "react";
import {
  getWelcomeKitQueue,
  releaseWelcomeKit,
  setKitBuyerPickup,
  type WelcomeKitRow,
} from "../../lib/api";
import GuestStudioTabs from "../../components/admin/GuestStudioTabs";
import { useIsMobile } from "../../lib/useIsMobile";

/** Pending kits older than this get the red age badge. Prod already carries
 *  rows from mid-July 2026, so "stale" needs to be visible, not just sortable. */
const STALE_DAYS = 30;

const daysSince = (iso: string | null): number | null => {
  if (!iso) return null;
  const ms = Date.now() - new Date(iso).getTime();
  return Number.isFinite(ms) ? Math.max(0, Math.floor(ms / 86_400_000)) : null;
};

const fmtDate = (iso: string | null, withTime = false): string => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("en-US", {
      timeZone: "America/Chicago",
      month: "short",
      day: "numeric",
      year: "numeric",
      ...(withTime ? { hour: "numeric", minute: "2-digit", hour12: true } : {}),
    });
  } catch {
    return iso;
  }
};

/** gift_delivery_date is a plain date (no tz) and gift_delivery_time a plain
 *  time — render them literally rather than via Date, which would shift them. */
const fmtDelivery = (date: string | null, time: string | null): string => {
  if (!date) return "immediately";
  const t = time ? ` at ${time.slice(0, 5)}` : "";
  return `${date}${t}`;
};

/** Button labels are tight — truncate long names in the label only. The
 *  confirm() dialog always shows the full name. */
const truncateName = (name: string, max = 18): string =>
  name.length > max ? `${name.slice(0, max - 1)}…` : name;

const PURCHASER_SUFFIX = " (purchaser)";

export default function AdminWelcomeKits() {
  // Below 1024 only (phones, iPad portrait — the page was 769px wide at 375 and 768): kit lines and
  // the gift-from line wrap. At 1024+ the styles are exactly the original ones, so the station
  // tablet's layout cannot change — wrapping there moved the status block under the name whenever
  // name + status were a few px too wide, where the original squeezes them.
  const isNarrow = useIsMobile(1024);
  // 1024+: a kit line keeps master's one-row layout unless it actually overflows (a long staff
  // name or Square order id ran the page to 1288px). Measured, not guessed from text length:
  // glyph widths vary ~2x, so any character limit either misses real overflows or wraps rows
  // that fit. A rule applied to every row would change near-fit ordinary rows (L121). Rows are
  // only ever added while the width and data are unchanged, so a wrapped row (which then fits)
  // can't flip back and forth; a resize or new data clears the set and re-measures before paint.
  const lineRefs = useRef(new Map<string, HTMLLabelElement>());
  const [overflowing, setOverflowing] = useState<Set<string>>(() => new Set());
  useLayoutEffect(() => {
    if (isNarrow) return;
    const add = [...lineRefs.current].filter(([id, el]) => !overflowing.has(id) && el.scrollWidth > el.clientWidth + 1).map(([id]) => id);
    if (add.length) setOverflowing((prev) => new Set([...prev, ...add]));
  });
  useEffect(() => {
    if (isNarrow) return;
    const onResize = () => setOverflowing(new Set());
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, [isNarrow]);
  const [activeTab, setActiveTab] = useState<"pending" | "released">("pending");
  const [pendingRows, setPendingRows] = useState<WelcomeKitRow[]>([]);
  const [releasedRows, setReleasedRows] = useState<WelcomeKitRow[]>([]);
  // New data (a refresh or a release) can shorten a line that was wrapped, so start over and
  // let the layout effect measure the new rows before paint.
  useLayoutEffect(() => {
    setOverflowing((prev) => (prev.size ? new Set() : prev));
  }, [pendingRows, releasedRows]);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [actionInProgress, setActionInProgress] = useState<string | null>(null);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  // Per-membership release form state: which pending items are ticked, and an
  // optional POS ticket id to stamp on the rows. Unticked = all pending.
  const [deselected, setDeselected] = useState<Record<string, Set<string>>>({});
  const [ticketIds, setTicketIds] = useState<Record<string, string>>({});

  const loadData = async () => {
    setLoading(true);
    setErrorMsg(null);
    try {
      const [pending, released] = await Promise.all([
        getWelcomeKitQueue("pending", 300),
        getWelcomeKitQueue("released", 100),
      ]);
      setPendingRows(pending || []);
      setReleasedRows(released || []);
    } catch (err: any) {
      setErrorMsg(err?.message || "Failed to load the welcome-kit queue");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  const toggleItem = (membershipId: string, itemId: string) => {
    setDeselected((prev) => {
      const next = { ...prev };
      const set = new Set(next[membershipId] || []);
      if (set.has(itemId)) set.delete(itemId);
      else set.add(itemId);
      next[membershipId] = set;
      return next;
    });
  };

  const handleRelease = async (row: WelcomeKitRow, releasedTo: "purchaser" | "member") => {
    const pendingIds = row.items.filter((i) => i.status === "pending").map((i) => i.id);
    const skip = deselected[row.membership_id] || new Set<string>();
    const chosen = pendingIds.filter((id) => !skip.has(id));
    if (!chosen.length) {
      alert("Tick at least one item to release.");
      return;
    }
    const partial = chosen.length !== pendingIds.length;
    const who = releasedTo === "purchaser" ? `${row.purchaser_name || "the gift buyer"} (gift buyer)` : row.recipient_name;
    if (!confirm(`Hand ${chosen.length} of ${pendingIds.length} welcome-kit item(s) to ${who}?${partial ? "\n\nThe rest stay pending." : ""}`)) return;

    setActionInProgress(row.membership_id);
    try {
      const res = await releaseWelcomeKit(row.membership_id, {
        itemIds: partial ? chosen : null,
        squareOrderId: (ticketIds[row.membership_id] || "").trim() || null,
        releasedTo,
      });
      if (res.ok) {
        setDeselected((prev) => { const n = { ...prev }; delete n[row.membership_id]; return n; });
        setTicketIds((prev) => { const n = { ...prev }; delete n[row.membership_id]; return n; });
        await loadData();
      } else {
        alert("Release failed: " + (res.error || "Unknown error"));
      }
    } catch (err: any) {
      alert("Error releasing kit: " + (err?.message || "Network error"));
    } finally {
      setActionInProgress(null);
    }
  };

  const handleToggleFlag = async (row: WelcomeKitRow) => {
    const enable = !row.buyer_pickup;
    const msg = enable
      ? `Flag this gift so ${row.purchaser_name || "the buyer"} can take the welcome kit? Their POS ticket will auto-comp the items (they must be attached to the ticket as the customer).`
      : `Remove the buyer-pickup flag? The kit goes back to recipient pickup (after activation).`;
    if (!confirm(msg)) return;
    setActionInProgress(row.membership_id);
    try {
      const res = await setKitBuyerPickup(row.membership_id, enable);
      if (res.ok) {
        await loadData();
      } else {
        alert("Could not update flag: " + (res.error || "Unknown error"));
      }
    } catch (err: any) {
      alert("Error updating flag: " + (err?.message || "Network error"));
    } finally {
      setActionInProgress(null);
    }
  };

  const buyerFlaggedCount = useMemo(() => pendingRows.filter((r) => r.buyer_pickup).length, [pendingRows]);
  const itemsPendingCount = useMemo(() => pendingRows.reduce((n, r) => n + (r.pending_count || 0), 0), [pendingRows]);
  const staleCount = useMemo(
    () => pendingRows.filter((r) => (daysSince(r.oldest_pending_at) ?? 0) >= STALE_DAYS).length,
    [pendingRows]
  );

  const filteredRows = useMemo(() => {
    const source = activeTab === "pending" ? pendingRows : releasedRows;
    const q = search.trim().toLowerCase();
    if (!q) return source;
    return source.filter((r) =>
      (r.recipient_name || "").toLowerCase().includes(q) ||
      (r.purchaser_name || "").toLowerCase().includes(q) ||
      (r.member_number != null && String(r.member_number).includes(q)) ||
      r.items.some((i) => (i.item_name || "").toLowerCase().includes(q))
    );
  }, [activeTab, pendingRows, releasedRows, search]);

  return (
    <div style={styles.container}>
      <GuestStudioTabs activeTab="kits" badgeCounts={{ kits: pendingRows.length }} />

      <div style={styles.header}>
        <div>
          <h1 style={styles.title}>Welcome Kits</h1>
          <p style={styles.subtitle}>
            C33 welcome-gift items available for pickup, who is authorized to take them, and the POS ticket they were comped on.
          </p>
        </div>
        <button style={styles.refreshBtn} onClick={loadData} disabled={loading}>
          {loading ? "Refreshing..." : "↻ Refresh"}
        </button>
      </div>

      {errorMsg && <div style={styles.errorBanner}>{errorMsg}</div>}

      <div style={styles.statsGrid}>
        <div style={styles.statCard}>
          <div style={styles.statVal}>{pendingRows.length}</div>
          <div style={styles.statLabel}>Awaiting pickup</div>
        </div>
        <div style={styles.statCard}>
          <div style={{ ...styles.statVal, color: "#C084FC" }}>{buyerFlaggedCount}</div>
          <div style={styles.statLabel}>Buyer taking kit</div>
        </div>
        <div style={styles.statCard}>
          <div style={styles.statVal}>{itemsPendingCount}</div>
          <div style={styles.statLabel}>Items pending</div>
        </div>
        <div style={styles.statCard}>
          <div style={{ ...styles.statVal, color: staleCount ? "var(--red, #E05040)" : "var(--green, #27ae60)" }}>{staleCount}</div>
          <div style={styles.statLabel}>Waiting {STALE_DAYS}+ days</div>
        </div>
      </div>

      <div style={styles.tabBar}>
        <button
          style={{ ...styles.tabBtn, ...(activeTab === "pending" ? styles.tabBtnActive : {}) }}
          onClick={() => setActiveTab("pending")}
        >
          Available for pickup
          <span style={{ ...styles.badge, background: "var(--gold, #F9AF36)", color: "#111" }}>{pendingRows.length}</span>
        </button>
        <button
          style={{ ...styles.tabBtn, ...(activeTab === "released" ? styles.tabBtnActive : {}) }}
          onClick={() => setActiveTab("released")}
        >
          Released
          <span style={{ ...styles.badge, background: "rgba(255,255,255,0.08)", color: "var(--muted, #8A7A66)" }}>{releasedRows.length}</span>
        </button>
      </div>

      {activeTab === "pending" && (
        <div style={styles.helpBox}>
          <strong>How the comp works.</strong> Ring the kit items on the guest's ticket with them attached as the Square customer.
          The webhook applies a 100% <em>C33 Welcome Gift</em> line discount and marks the items released when the ticket closes —
          inventory moves because the items were actually sold at $0. For a <strong>gift buyer</strong> taking the kit, the flag below must
          be on and the online gift purchase must already exist <em>before</em> the items are rung (or re-save the ticket).
          If the guest isn't attached to the ticket, comp the lines by hand — turn on <strong>Buyer takes kit</strong> if the gift
          buyer is taking it, then use <strong>Hand kit to …</strong> — that choice, not the flag, is what the member's account page
          reports as who picked up the kit. The two Glencairn glasses can be one line at quantity 2, or two lines if the member picks two different styles — both are comped against the same kit row.
        </div>
      )}

      <div style={styles.toolbar}>
        <input
          type="text"
          style={styles.search}
          placeholder="Search recipient, gifter, member #, item…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      </div>

      {loading ? (
        <div style={styles.empty}>Loading welcome-kit queue…</div>
      ) : filteredRows.length === 0 ? (
        <div style={styles.empty}>
          <div style={{ fontSize: 32, marginBottom: 8 }}>{activeTab === "pending" ? "🎁" : "📋"}</div>
          {activeTab === "pending" ? "No welcome kits waiting for pickup." : "No released kits on record."}
        </div>
      ) : (
        <div style={styles.list}>
          {filteredRows.map((row) => {
            const isGift = row.membership_type === "gift";
            const age = daysSince(row.oldest_pending_at);
            const stale = age != null && age >= STALE_DAYS;
            const pendingItems = row.items.filter((i) => i.status === "pending");
            const skip = deselected[row.membership_id] || new Set<string>();
            const chosenCount = pendingItems.filter((i) => !skip.has(i.id)).length;
            const busy = actionInProgress === row.membership_id;
            const needsActivation = row.membership_status === "purchased" && !row.buyer_pickup;
            const blockedStatus = !["active", "purchased"].includes(row.membership_status);
            const canToggle = isGift && !!row.purchaser_customer_id && pendingItems.length > 0 && !blockedStatus;
            const canReleaseToBuyer = isGift && !!row.purchaser_customer_id && row.buyer_pickup;
            const releaseDisabled = busy || blockedStatus || needsActivation || !chosenCount;
            const releaseDisabledTitle = needsActivation
              ? "Disabled — recipient must activate first (or turn on Buyer takes kit)"
              : blockedStatus
              ? `Disabled — membership is ${row.membership_status}`
              : !chosenCount
              ? "Choose at least one item"
              : null;

            return (
              <div
                key={row.membership_id}
                style={{
                  ...styles.card,
                  borderLeft: row.buyer_pickup
                    ? "4px solid #C084FC"
                    : stale
                    ? "4px solid var(--red, #E05040)"
                    : "4px solid var(--gold, #F9AF36)",
                }}
              >
                <div style={styles.cardHeader}>
                  <div style={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: 6 }}>
                    <span style={styles.cardTitle}>{row.recipient_name}</span>
                    {row.member_number != null && <span style={styles.memberNo}>#{row.member_number}</span>}
                    {row.buyer_pickup ? (
                      <span style={{ ...styles.tag, ...styles.tagBuyer }}>🎁 Buyer takes kit</span>
                    ) : (
                      <span style={{ ...styles.tag, ...styles.tagRecipient }}>{isGift ? "Recipient pickup" : "Member pickup"}</span>
                    )}
                    <span style={styles.tagSource}>{row.membership_status}</span>
                    {activeTab === "pending" && age != null && (
                      <span style={{ ...styles.tagSource, ...(stale ? styles.tagStale : {}) }}>
                        {age === 0 ? "today" : `${age}d waiting`}
                      </span>
                    )}
                  </div>
                  <div style={styles.cardCount}>
                    {activeTab === "pending" ? `${row.pending_count} pending` : `released ${fmtDate(row.picked_up_at)}`}
                  </div>
                </div>

                <div style={isNarrow ? { ...styles.cardMeta, overflowWrap: "anywhere" } : styles.cardMeta}>
                  {isGift ? (
                    <>
                      Gift from <strong style={{ color: "var(--text, #F0E6D4)" }}>{row.purchaser_name || "unknown gifter"}</strong>
                      &nbsp;·&nbsp; purchased {fmtDate(row.joined_at)}
                      &nbsp;·&nbsp; reveal email {fmtDelivery(row.gift_delivery_date, row.gift_delivery_time)}
                    </>
                  ) : (
                    <>Joined {fmtDate(row.joined_at)}</>
                  )}
                  {row.activated_at && <>&nbsp;·&nbsp; activated {fmtDate(row.activated_at)}</>}
                  {row.buyer_pickup && row.buyer_pickup_set_at && <>&nbsp;·&nbsp; flagged {fmtDate(row.buyer_pickup_set_at, true)}</>}
                </div>

                {activeTab === "pending" && needsActivation && (
                  <div style={styles.noteBox}>
                    {isGift && row.purchaser_customer_id ? (
                      <>
                        Recipient must activate before pickup. If {row.purchaser_name || "the gift buyer"} is taking the kit to
                        give in person, turn on <em>Buyer takes kit</em> first.
                      </>
                    ) : (
                      "Recipient must activate before pickup."
                    )}
                  </div>
                )}
                {activeTab === "pending" && blockedStatus && (
                  <div style={{ ...styles.noteBox, borderColor: "rgba(224,80,64,0.4)", color: "#ff8b7b" }}>
                    Membership is {row.membership_status} — kit items cannot be released.
                  </div>
                )}

                <div style={styles.linesBox}>
                  {row.items.map((item) => {
                    const isPending = item.status === "pending";
                    const wrapLine = isNarrow || overflowing.has(item.id);
                    const ticked = isPending && !skip.has(item.id);
                    return (
                      <label key={item.id} ref={(el) => { if (el) lineRefs.current.set(item.id, el); else lineRefs.current.delete(item.id); }} style={{ ...styles.lineItem, ...(wrapLine ? narrowLine : {}), cursor: isPending && activeTab === "pending" ? "pointer" : "default" }}>
                        <span style={{ display: "flex", alignItems: "center", gap: 8 }}>
                          {activeTab === "pending" && isPending && (
                            <input
                              type="checkbox"
                              checked={ticked}
                              disabled={busy || blockedStatus}
                              onChange={() => toggleItem(row.membership_id, item.id)}
                            />
                          )}
                          <span>
                            <strong>{item.item_name}</strong>
                            {item.quantity > 1 && <span style={{ color: "var(--muted, #8A7A66)" }}> × {item.quantity}</span>}
                          </span>
                        </span>
                        <span style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 11, color: "var(--muted, #8A7A66)", ...(wrapLine ? { flexWrap: "wrap", minWidth: 0 } : {}) }}>
                          {item.status === "fulfilled" ? (
                            <>
                              <span style={{ ...styles.tag, ...styles.tagDone }}>released</span>
                              {item.picked_up_by && (
                                item.picked_up_by.endsWith(PURCHASER_SUFFIX) ? (
                                  <span style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>
                                    <span>{item.picked_up_by.slice(0, -PURCHASER_SUFFIX.length)}</span>
                                    <span style={{ ...styles.tag, ...styles.tagBuyer }}>→ gift buyer</span>
                                  </span>
                                ) : (
                                  <span>{item.picked_up_by}</span>
                                )
                              )}
                              {item.picked_up_at && <span>{fmtDate(item.picked_up_at, true)}</span>}
                              {item.square_order_id && <code style={styles.code}>{item.square_order_id}</code>}
                            </>
                          ) : item.status === "canceled" ? (
                            <span style={{ ...styles.tag, ...styles.tagCanceled }}>canceled</span>
                          ) : (
                            <span style={{ ...styles.tag, ...styles.tagPending }}>pending</span>
                          )}
                        </span>
                      </label>
                    );
                  })}
                </div>

                {activeTab === "pending" && (
                  <div style={styles.cardFooter}>
                    <input
                      type="text"
                      style={styles.ticketInput}
                      placeholder="POS ticket / order id (optional)"
                      value={ticketIds[row.membership_id] || ""}
                      disabled={busy}
                      onChange={(e) => setTicketIds((prev) => ({ ...prev, [row.membership_id]: e.target.value }))}
                    />
                    <button
                      style={{ ...styles.btnSecondary, ...(canToggle ? {} : styles.btnDisabled) }}
                      disabled={busy || !canToggle}
                      title={
                        !isGift ? "Only gift memberships have a buyer"
                        : !row.purchaser_customer_id ? "No purchaser on record — the buyer can't be matched at the POS; use Mark released"
                        : blockedStatus ? `Membership is ${row.membership_status}`
                        : row.buyer_pickup ? "Return the kit to recipient pickup" : "Let the gift buyer take the kit now"
                      }
                      onClick={() => handleToggleFlag(row)}
                    >
                      {row.buyer_pickup ? "✕ Buyer not taking kit" : "🎁 Buyer takes kit"}
                    </button>
                    {canReleaseToBuyer ? (
                      <>
                        <button
                          style={{
                            ...styles.btnPrimary,
                            ...(releaseDisabled ? styles.btnDisabled : {}),
                          }}
                          disabled={releaseDisabled}
                          title={
                            releaseDisabledTitle ||
                            `Hand the kit to the gift buyer, ${row.purchaser_name || "unknown"} — the member's account page will say it was handed to the person who gifted the membership`
                          }
                          onClick={() => handleRelease(row, "purchaser")}
                        >
                          {busy ? "Working…" : `Hand kit to ${truncateName(row.purchaser_name || "gift buyer")} (buyer) (${chosenCount}/${pendingItems.length})`}
                        </button>
                        <button
                          style={{
                            ...styles.btnSecondary,
                            ...(releaseDisabled ? styles.btnDisabled : {}),
                          }}
                          disabled={releaseDisabled}
                          title={
                            releaseDisabledTitle ||
                            `Hand the kit to the recipient, ${row.recipient_name} — recorded as a member pickup`
                          }
                          onClick={() => handleRelease(row, "member")}
                        >
                          {busy ? "Working…" : `Hand kit to ${truncateName(row.recipient_name)} (${chosenCount}/${pendingItems.length})`}
                        </button>
                      </>
                    ) : (
                      <button
                        style={{ ...styles.btnPrimary, ...(releaseDisabled ? styles.btnDisabled : {}) }}
                        disabled={releaseDisabled}
                        title={releaseDisabledTitle || undefined}
                        onClick={() => handleRelease(row, "member")}
                      >
                        {busy ? "Working…" : `Mark released to ${truncateName(row.recipient_name)} (${chosenCount}/${pendingItems.length})`}
                      </button>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// Kit line below 1024: name and status block wrap onto two lines instead of widening the page.
const narrowLine: CSSProperties = { flexWrap: "wrap", overflowWrap: "anywhere" };

const styles: Record<string, CSSProperties> = {
  container: { maxWidth: 1040, margin: "0 auto", padding: "24px 20px" },
  header: { display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20, flexWrap: "wrap", gap: 12 },
  title: { fontFamily: "Georgia, serif", fontSize: 24, color: "var(--gold, #F9AF36)", margin: "0 0 4px 0" },
  subtitle: { fontSize: 13, color: "var(--muted, #8A7A66)", margin: 0 },
  refreshBtn: {
    background: "var(--surface, #13100B)", border: "1px solid var(--border, #2A1F14)", color: "var(--text, #F0E6D4)",
    borderRadius: 6, padding: "8px 14px", fontSize: 13, fontWeight: 600, cursor: "pointer",
  },
  errorBanner: {
    background: "rgba(224, 80, 64, 0.15)", border: "1px solid rgba(224, 80, 64, 0.4)", color: "#ff8b7b",
    padding: "10px 14px", borderRadius: 6, marginBottom: 16, fontSize: 13,
  },
  statsGrid: { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: 12, marginBottom: 24 },
  statCard: { background: "var(--surface, #13100B)", border: "1px solid var(--border, #2A1F14)", borderRadius: 8, padding: "16px 12px", textAlign: "center" },
  statVal: { fontFamily: "Georgia, serif", fontSize: 26, fontWeight: 700, color: "var(--gold, #F9AF36)" },
  statLabel: { fontSize: 11, textTransform: "uppercase", letterSpacing: "0.1em", color: "var(--muted, #8A7A66)", marginTop: 4 },
  tabBar: { display: "flex", gap: 6, borderBottom: "1px solid var(--border, #2A1F14)", marginBottom: 16, overflowX: "auto" },
  tabBtn: {
    padding: "10px 16px", fontSize: 13, fontWeight: 600, color: "var(--muted, #8A7A66)", cursor: "pointer",
    background: "none", border: "none", borderBottom: "2px solid transparent", display: "flex", alignItems: "center", gap: 6, whiteSpace: "nowrap",
  },
  tabBtnActive: { color: "var(--gold, #F9AF36)", borderBottomColor: "var(--gold, #F9AF36)" },
  badge: { padding: "2px 6px", borderRadius: 10, fontSize: 11, fontWeight: 700 },
  helpBox: {
    background: "rgba(249, 175, 54, 0.06)", border: "1px solid rgba(249, 175, 54, 0.25)", borderRadius: 8,
    padding: "10px 14px", fontSize: 12.5, lineHeight: 1.55, color: "var(--text, #F0E6D4)", marginBottom: 14,
  },
  noteBox: {
    background: "rgba(255,255,255,0.03)", border: "1px solid var(--border, #2A1F14)", borderRadius: 6,
    padding: "8px 12px", fontSize: 12, color: "var(--muted, #8A7A66)", marginBottom: 10,
  },
  toolbar: { marginBottom: 16, display: "flex", gap: 10 },
  search: {
    flex: 1, background: "var(--surface, #13100B)", border: "1px solid var(--border, #2A1F14)", borderRadius: 6,
    padding: "8px 12px", fontSize: 13, color: "var(--text, #F0E6D4)",
  },
  empty: {
    textAlign: "center", padding: "48px 24px", color: "var(--muted, #8A7A66)", background: "var(--surface, #13100B)",
    borderRadius: 8, border: "1px solid var(--border, #2A1F14)",
  },
  list: { display: "flex", flexDirection: "column", gap: 12 },
  card: { background: "var(--surface, #13100B)", border: "1px solid var(--border, #2A1F14)", borderRadius: 8, padding: 16 },
  cardHeader: { display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 8, marginBottom: 6 },
  cardTitle: { fontSize: 15, fontWeight: 700, color: "var(--text, #F0E6D4)" },
  memberNo: { fontFamily: "monospace", fontSize: 12, color: "var(--gold, #F9AF36)" },
  cardCount: { fontFamily: "Georgia, serif", fontSize: 14, fontWeight: 700, color: "var(--gold, #F9AF36)" },
  cardMeta: { fontSize: 12, color: "var(--muted, #8A7A66)", marginBottom: 10 },
  code: { fontFamily: "monospace", color: "var(--gold, #F9AF36)" },
  tag: { display: "inline-block", padding: "2px 8px", borderRadius: 4, fontSize: 11, fontWeight: 700, textTransform: "uppercase" },
  tagBuyer: { background: "rgba(192, 132, 252, 0.15)", color: "#C084FC", border: "1px solid rgba(192, 132, 252, 0.35)" },
  tagRecipient: { background: "rgba(249, 175, 54, 0.15)", color: "var(--gold, #F9AF36)", border: "1px solid rgba(249, 175, 54, 0.3)" },
  tagPending: { background: "rgba(249, 175, 54, 0.12)", color: "var(--gold, #F9AF36)" },
  tagDone: { background: "rgba(39, 174, 96, 0.15)", color: "var(--green, #27ae60)" },
  tagCanceled: { background: "rgba(255,255,255,0.05)", color: "var(--muted, #8A7A66)" },
  tagSource: {
    display: "inline-block", padding: "2px 6px", borderRadius: 4, fontSize: 10, textTransform: "uppercase",
    background: "rgba(255, 255, 255, 0.05)", color: "var(--muted, #8A7A66)", border: "1px solid var(--border, #2A1F14)",
  },
  tagStale: { background: "rgba(224, 80, 64, 0.15)", color: "#ff7666", borderColor: "rgba(224, 80, 64, 0.3)" },
  linesBox: { background: "rgba(0, 0, 0, 0.2)", borderRadius: 6, padding: "6px 12px", marginBottom: 12, fontSize: 12 },
  lineItem: { display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10, padding: "6px 0", borderBottom: "1px solid rgba(255, 255, 255, 0.04)" },
  cardFooter: { display: "flex", justifyContent: "flex-end", alignItems: "center", gap: 8, flexWrap: "wrap" },
  ticketInput: {
    flex: "1 1 220px", minWidth: 180, background: "var(--surface, #13100B)", border: "1px solid var(--border, #2A1F14)",
    borderRadius: 5, padding: "6px 10px", fontSize: 12, color: "var(--text, #F0E6D4)",
  },
  btnPrimary: { background: "var(--gold, #F9AF36)", color: "#111", border: "none", borderRadius: 5, padding: "6px 14px", fontSize: 12, fontWeight: 700, cursor: "pointer" },
  btnSecondary: { background: "transparent", color: "var(--text, #F0E6D4)", border: "1px solid var(--border, #2A1F14)", borderRadius: 5, padding: "6px 14px", fontSize: 12, fontWeight: 600, cursor: "pointer" },
  btnDisabled: {
    opacity: 0.4,
    cursor: "not-allowed",
    background: "rgba(42, 31, 20, 0.6)",
    color: "var(--muted, #8A7A66)",
  },
};
